import { Code2, Copy, Link2, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetPublicClassShareLink } from '~entities/classes/api/get-public-class-share-link';
import { useRotatePublicClassShareLink } from '~entities/classes/api/rotate-public-class-share-link';
import type { ClassDetails } from '~entities/classes/modal/class';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { copyToClipboard } from '~shared/utils/clipboard';

import {
  buildClassPublicShareEmbedUrl,
  buildClassPublicShareIframeEmbedHtml,
  buildClassPublicShareUrl,
} from './build-class-public-share-url';

interface ClassPublicShareLinkSectionProps {
  classId: string;
  classData: ClassDetails | undefined;
  /**
   * Current studio context. `locationId` (GHL) is only embedded when this
   * studio owns the class (`classData.studio === currentStudio._id`).
   */
  currentStudio:
    | {
        _id?: string;
        locationId?: string;
      }
    | undefined;
}

export function ClassPublicShareLinkSection({
  classId,
  classData,
  currentStudio,
}: ClassPublicShareLinkSectionProps) {
  const mutation = useRotatePublicClassShareLink(classId);
  const shareLinkQuery = useGetPublicClassShareLink(classId);
  /** Soft cache for the token returned by generate/regenerate before query cache settles. */
  const [knownShareToken, setKnownShareToken] = useState('');

  useEffect(() => {
    setKnownShareToken('');
  }, [classId]);

  const classShareToken =
    typeof classData?.publicClassShareToken === 'string'
      ? classData.publicClassShareToken.trim()
      : '';
  const fetchedShareToken =
    typeof shareLinkQuery.data?.shareToken === 'string'
      ? shareLinkQuery.data.shareToken.trim()
      : '';
  const mutationShareToken =
    typeof mutation.data?.shareToken === 'string'
      ? mutation.data.shareToken.trim()
      : '';
  const shareToken =
    knownShareToken ||
    mutationShareToken ||
    fetchedShareToken ||
    classShareToken;

  const portalBase = import.meta.env.VITE_PARENT_PORTAL_URL;
  const base =
    typeof portalBase === 'string' && portalBase.trim().length > 0
      ? portalBase.replace(/\/$/, '')
      : '';

  const classStudioMongoId =
    typeof classData?.studio === 'string' ? classData.studio.trim() : '';
  const currentStudioMongoId =
    typeof currentStudio?._id === 'string' ? currentStudio._id.trim() : '';
  const currentGhlLocationId =
    typeof currentStudio?.locationId === 'string'
      ? currentStudio.locationId.trim()
      : '';
  // Only attach studioId when the current studio context owns this class.
  const studioParam =
    classStudioMongoId.length > 0 &&
    currentStudioMongoId.length > 0 &&
    classStudioMongoId === currentStudioMongoId &&
    currentGhlLocationId.length > 0
      ? currentGhlLocationId
      : '';

  const shareUrl = useMemo(
    () =>
      buildClassPublicShareUrl({
        portalBase: base,
        shareToken,
        studioId: studioParam,
      }),
    [base, shareToken, studioParam],
  );

  const iframeHtml = useMemo(() => {
    const embedUrl = buildClassPublicShareEmbedUrl(shareUrl);
    return buildClassPublicShareIframeEmbedHtml(embedUrl);
  }, [shareUrl]);

  const portalBaseMissing = !base;
  const portalUrlInvalid = Boolean(base) && shareToken.length > 0 && !shareUrl;
  const isResolvingExistingLink =
    shareToken.length === 0 && shareLinkQuery.isLoading;

  const handleCopy = async () => {
    if (!shareUrl) {
      toast.error('Link not ready');
      return;
    }
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      toast.success('Link copied');
    } else {
      toast.error('Could not copy');
    }
  };

  const handleCopyEmbed = async () => {
    if (!iframeHtml) {
      toast.error('Embed code not ready');
      return;
    }
    const ok = await copyToClipboard(iframeHtml);
    if (ok) {
      toast.success('Embed code copied', {
        description: 'Paste it into an HTML or embed block on your website.',
      });
    } else {
      toast.error('Could not copy embed code');
    }
  };

  const handleGenerate = async () => {
    try {
      const result = await mutation.mutateAsync(false);
      const next =
        typeof result?.shareToken === 'string' ? result.shareToken.trim() : '';
      if (next.length === 0) {
        toast.error('Could not create link');
        return;
      }
      setKnownShareToken(next);
      toast.success('Public link created');
    } catch {
      toast.error('Could not create link');
    }
  };

  const handleRegenerate = async () => {
    const ok = window.confirm(
      'Regenerate this link? The previous URL will stop working.',
    );
    if (!ok) return;
    try {
      const result = await mutation.mutateAsync(true);
      const next =
        typeof result?.shareToken === 'string' ? result.shareToken.trim() : '';
      if (next.length === 0) {
        toast.error('Could not regenerate link');
        return;
      }
      setKnownShareToken(next);
      toast.success('New public link is ready');
    } catch {
      toast.error('Could not regenerate link');
    }
  };

  return (
    <div className='space-y-3 rounded-lg border border-border p-4'>
      <div className='flex items-start gap-2'>
        <Link2 className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
        <Label className='text-base'>Unique Public Class Page</Label>
      </div>

      {isResolvingExistingLink ? (
        <p className='text-sm text-muted-foreground'>Loading public link…</p>
      ) : shareToken.length === 0 ? (
        <Button
          type='button'
          variant='secondary'
          disabled={mutation.isPending}
          onClick={() => {
            void handleGenerate();
          }}
        >
          Generate public link
        </Button>
      ) : shareUrl ? (
        <div className='flex gap-2'>
          <Input readOnly value={shareUrl} className='font-mono text-xs' />
          <Button
            type='button'
            variant='secondary'
            size='icon'
            onClick={() => {
              void handleCopy();
            }}
            aria-label='Copy public class link'
            title='Copy public class link'
          >
            <Copy className='size-4' />
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            disabled={!iframeHtml}
            onClick={() => {
              void handleCopyEmbed();
            }}
            aria-label='Copy embed code'
            title='Copy embed code (HTML iframe)'
          >
            <Code2 className='size-4' />
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            disabled={mutation.isPending}
            onClick={() => {
              void handleRegenerate();
            }}
            aria-label='Regenerate public class link'
            title='Regenerate public class link'
          >
            <RefreshCw className='size-4' />
          </Button>
        </div>
      ) : (
        <div className='space-y-2'>
          <p className='text-sm text-destructive'>
            {portalBaseMissing
              ? 'Parent Portal URL is not configured; contact your administrator. You can still regenerate the share token.'
              : portalUrlInvalid
                ? 'Parent Portal URL is invalid, so the share link cannot be built. Check VITE_PARENT_PORTAL_URL (including https://).'
                : 'Share link could not be built.'}
          </p>
          <Button
            type='button'
            variant='secondary'
            size='sm'
            disabled={mutation.isPending}
            onClick={() => {
              void handleRegenerate();
            }}
          >
            <RefreshCw className='mr-2 size-4' />
            Regenerate token
          </Button>
        </div>
      )}
    </div>
  );
}
